import React, { memo, useEffect, useLayoutEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Typography, Skeleton } from "@telus-uds/components-web";
import useAuth from "./hooks/useAuth";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { useLanguage } from "./hooks/useLanguage";
import { userFromJwt } from "./lib/userFromJwt";
import { usePageViewTracking } from "./lib/pita/pageView";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ErrorMessagePage } from "./components/error/ErrorMessagePage";
import { ExternalRedirect } from "./components/ExternalRedirect";
import AdminPage from "./components/rbac/AdminPage";
import ApiDiscoveryPage from "./components/externalApis/ApiDiscoveryPage";
import { ApiMarketplacePage } from "./mfe/ApiMarketplacePage";
import { generateCcoeMfePage } from "./mfe/generateCcoeMfePage";
import { generateMfePage } from "./mfe/generateMfePage";
import { pageTitleFor } from "./pageTitles";
import { DarkAwareUdsProvider } from "./theme/udsDarkMode";
import { isLightOnlyPath, LIGHT_ONLY_ATTRIBUTE } from "./theme/lightOnly";

const SideNavMFE = generateMfePage("sidenav");
const AgentMFE = generateMfePage("agent");
const HomepageMFE = generateMfePage("homepage");
const ExampleMFE = generateMfePage("example");
const AppMarketplaceMFE = generateMfePage("app-marketplace");
const DashboardLibraryMFE = generateMfePage("dashboard-library");
const DocListMFE = generateMfePage("doc-list");
const DocViewerMFE = generateMfePage("doc-viewer");
const VideoLibraryMFE = generateMfePage("video-library");
const CioMentorshipMFE = generateMfePage("cio-mentorship");
const ExternalHealthCheckMFE = generateMfePage("external-health-check");
const EmbargoesMFE = generateMfePage("embargoes");
const IccMFE = generateMfePage("icc");
const TascMFE = generateMfePage("tasc");
const SecurityMetricsMFE = generateMfePage("security-metrics");
const AnalyticsMFE = generateMfePage("App");
const GithubIngestionTrackerMFE = generateMfePage("github-ingestion-tracker");
const AdminPortalMFE = generateCcoeMfePage("admin-portal");
const AwsManagementMFE = generateCcoeMfePage("aws-management");
const BillingChargeManagementMFE = generateCcoeMfePage("billing-charge-management");
const CostOptimizationMFE = generateCcoeMfePage("cost-optimization");
const GroupsManagementMFE = generateCcoeMfePage("groups-management");
const NamespaceGeneratorMFE = generateCcoeMfePage("namespace-generator");
const OnboardPluginMFE = generateCcoeMfePage("onboard-plugin");
const ServiceAccountKeyMFE = generateCcoeMfePage("service-account-key");
const SlotReservationMFE = generateCcoeMfePage("slot-reservation");
const MetadataSearchToolMFE = generateMfePage("metadata-search-tool");
const ReleasesMFE = generateMfePage("release-page");
const TurbopufferExplorerMFE = generateMfePage("turbopuffer-explorer");
const AacLandingMFE = generateMfePage("aac-landing");
const McpSetupMFE = generateMfePage("mcp-setup");
const ApiKeyManagerMFE = generateMfePage("api-key-manager");
const EapOnboardMFE = generateMfePage("eap-onboard");
const SroPortalMFE = generateMfePage("sro-portal");
const HaproxyFrontDoorMFE = generateMfePage("haproxy-front-door");
const F5FrontDoorMFE = generateMfePage("f5-front-door");
const ProfileMFE = generateMfePage("profile");
const ContributeMFE = generateMfePage("contribute");
const MfeLiteMFE = generateMfePage("mfe-lite");
const WaskMFE = generateMfePage("wask");
const AacTemplateMFE = generateMfePage("aac-template");
const TranslationsToolMFE = generateMfePage("translations-tool");
const MultiLanguageTranslationMFE = generateMfePage("multi-language-translation");
const ScaffolderMFE = generateMfePage("scaffolder");

// SPOC has migrated to an external site; these routes redirect there.
const SPOC_EXTERNAL_URL = "https://spoc.cloudapps.telus.com/";

const TRANSLATOR_PATHS = [
  "/translations-tool",
  "/telus-translator",
  "/telustranslator",
  "/gpt/copilots/e9c45e40-20f5-4aa1-9bbc-b112739037f5",
];

const MemoizedSideNav = memo(
  SideNavMFE,
  (prev, next) => prev.jwt === next.jwt && prev.lang === next.lang
);

const MemoizedAgent = memo(
  AgentMFE,
  (prev, next) => prev.jwt === next.jwt && prev.lang === next.lang
);

function RouterContent() {
  const navigate = useNavigate();
  const authState = useAuth();
  const { language } = useLanguage();

  const { pathname } = useLocation();
  usePageViewTracking(pathname, authState.jwt);

  useEffect(() => {
    const title = pageTitleFor(pathname);
    if (title) document.title = title;
  }, [pathname]);

  const isLightOnly = isLightOnlyPath(pathname);
  // Layout effect so the container flips before paint, not after one dark frame.
  useLayoutEffect(() => {
    if (!isLightOnly) return undefined;
    document.documentElement.setAttribute(LIGHT_ONLY_ATTRIBUTE, "");
    return () => document.documentElement.removeAttribute(LIGHT_ONLY_ATTRIBUTE);
  }, [isLightOnly]);

  const userInfo = useMemo(() => {
    const user = userFromJwt(authState.jwt);
    return user && { ...user, identifier: user.userEntityRef };
  }, [authState.jwt]);

  return (
    <div className="app-container">
      <div className="app-sidenav">
        <MemoizedSideNav
          jwt={authState.jwt}
          lang={language}
          onNavigate={(path: string) => void navigate(path)}
        />
      </div>
      <main className="app-main">
        {/* MFE routes are prefixes: remotes route within their own path, which Backstage's
            FlatRoutes allowed for every route implicitly. */}
        <Routes>
          <Route path="/" element={<ErrorBoundary><HomepageMFE /></ErrorBoundary>} />
          <Route path="/example/*" element={<ExampleMFE />} />
          <Route path="/app-marketplace/*" element={<AppMarketplaceMFE />} />
          <Route path="/dashboard-library/*" element={<DashboardLibraryMFE />} />
          <Route path="/docs" element={<DocListMFE />} />
          <Route path="/docs/:repoName/*" element={<DocViewerMFE />} />
          <Route path="/video-library/*" element={<VideoLibraryMFE />} />
          <Route
            path="/cio-tec-mentorship/*"
            element={userInfo ? <CioMentorshipMFE user={userInfo} /> : null}
          />
          <Route path="/external-health-check/*" element={<ExternalHealthCheckMFE />} />
          <Route path="/embargoes/*" element={<EmbargoesMFE />} />
          <Route path="/icc/*" element={<IccMFE />} />
          <Route path="/tasc/*" element={<TascMFE />} />
          <Route path="/security-metrics/*" element={<SecurityMetricsMFE />} />
          <Route path="/analytics/*" element={<AnalyticsMFE />} />
          <Route path="/github-ingestion-tracker/*" element={<GithubIngestionTrackerMFE />} />
          <Route path="/mcp/*" element={<McpSetupMFE />} />
          <Route path="/mcp/keys" element={<ApiKeyManagerMFE />} />
          <Route path="/api-marketplace/*" element={<ApiMarketplacePage />} />
          <Route path="/eap-onboard/*" element={<EapOnboardMFE />} />
          <Route path="/lb-request/*" element={<F5FrontDoorMFE />} />
          <Route path="/haproxy-front-door/*" element={<HaproxyFrontDoorMFE />} />
          <Route path="/profile/*" element={<ProfileMFE />} />
          <Route path="/settings" element={<Navigate to="/profile" replace />} />
          <Route path="/contribute/*" element={<ContributeMFE />} />
          <Route path="/mfe-lite/*" element={<MfeLiteMFE />} />
          <Route path="/wask/*" element={<WaskMFE />} />
          <Route path="/aac-template/*" element={<AacTemplateMFE />} />
          <Route path="/example-mfe/*" element={<ExampleMFE />} />
          <Route path="/api-marketplace-mfe/*" element={<ApiMarketplacePage />} />
          <Route path="/multi-language-translation/*" element={<MultiLanguageTranslationMFE />} />
          {TRANSLATOR_PATHS.map(path => (
            <Route key={path} path={`${path}/*`} element={<TranslationsToolMFE />} />
          ))}
          <Route path="/sro-portal/*" element={<SroPortalMFE />} />
          <Route path="/metadata-search/*" element={<MetadataSearchToolMFE />} />
          <Route path="/metadataSearchTool/*" element={<MetadataSearchToolMFE />} />
          <Route path="/releases/*" element={<ReleasesMFE />} />
          <Route path="/turbopuffer-explorer/*" element={<TurbopufferExplorerMFE />} />
          <Route path="/aac-template-landing/*" element={<AacLandingMFE />} />
          {/* CCoE — nested under /cloud-management to match legacy routing */}
          <Route path="/cloud-management/*" element={<OnboardPluginMFE />} />
          <Route path="/cloud-management/aws/*" element={<AwsManagementMFE />} />
          <Route path="/cloud-management/admin/*" element={<AdminPortalMFE />} />
          <Route path="/cloud-management/slot-reservation/*" element={<SlotReservationMFE />} />
          <Route path="/cloud-management/service-account-key/*" element={<ServiceAccountKeyMFE />} />
          <Route path="/cloud-management/namespace/*" element={<NamespaceGeneratorMFE />} />
          <Route path="/cloud-management/groups-management/*" element={<GroupsManagementMFE />} />
          <Route path="/cloud-management/cost-optimization-gatekeeper/*" element={<CostOptimizationMFE />} />
          <Route path="/cloud-management/billing-charge-management/*" element={<BillingChargeManagementMFE />} />
          <Route path="/spoc" element={<ExternalRedirect url={SPOC_EXTERNAL_URL} />} />
          <Route path="/spoc/*" element={<ExternalRedirect url={SPOC_EXTERNAL_URL} />} />
          <Route path="/gpt/copilots/spoc" element={<ExternalRedirect url={SPOC_EXTERNAL_URL} />} />
          <Route
            path="/admin"
            element={
              <DarkAwareUdsProvider>
                <AdminPage />
              </DarkAwareUdsProvider>
            }
          />
          <Route
            path="/api-registry"
            element={
              <DarkAwareUdsProvider>
                <ApiDiscoveryPage />
              </DarkAwareUdsProvider>
            }
          />
          {/* The legacy Backstage scaffolder, a React 18 remote from simplify-mfe-components. */}
          <Route path="/create/*" element={<ScaffolderMFE />} />
          <Route path="*" element={<ErrorMessagePage />} />
        </Routes>
      </main>
      <ErrorBoundary fallback={null}>
        <MemoizedAgent jwt={authState.jwt} lang={language} />
      </ErrorBoundary>
    </div>
  );
}

function AppContent() {
  const authState = useAuth();

  if (authState.error) {
    return (
      <div className="app-container">
        <div className="error-container">
          <Typography variant={{ size: "h2" }}>Authentication Error</Typography>
          <Typography variant={{ size: "body" }}>{authState.error}</Typography>
        </div>
      </div>
    );
  }

  if (authState.isLoading || !authState.isAuthenticated) {
    return (
      <div className="app-container">
        <div className="app-watermark">
          <Typography variant={{ size: "display1" }} tokens={{ color: "#f4f4f7" }}>
            simplify
          </Typography>
        </div>
        <Skeleton />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
